import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';

import type { AddressDetails } from '@/components/address-autocomplete';
import type {
  IPluginDetailResponse,
  IUpdatePluginRequest,
} from '@/providers/HttpProvider/plugins/MLParcel/types';
import type { MuiTelInputInfo } from 'mui-tel-input';

import { PLATFORM_NAME } from './constants';

interface UseMLParcelConfigProps {
  pluginId: string;
  clientId: string;
  isOpen: boolean;
}

const normalizePhone = (phone: string) => {
  if (!phone) return '';
  if (phone.startsWith('+')) return phone;
  const digits = phone.replace(/\D/g, '');
  return `+52 ${digits}`;
};

const useMLParcelConfig = (props: UseMLParcelConfigProps) => {
  const { pluginId, clientId, isOpen } = props;
  const [useCustomAddress, setUseCustomAddress] = useState(false);
  const [checkOptions, setCheckOptions] = useState({ createOrders: true });
  const [autoCreateLogisticModes, setAutoCreateLogisticModes] = useState<string[]>([]);
  const [processOrdersWebhook, setProcessOrdersWebhook] = useState(false);
  const [storeInfo, setStoreInfo] = useState({
    storeName: '',
    storeEmail: '',
    sellerId: '',
    storePhone: '',
  });
  const [phoneError, setPhoneError] = useState(false);
  const [isWebhookPrimary, setIsWebhookPrimary] = useState(false);
  const [addressData, setAddressData] = useState({
    address: '',
    country: 'MEX',
    latitude: 0,
    longitude: 0,
    zipCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onToggleAddress = () => setUseCustomAddress((prev) => !prev);

  const onChangeStoreInfo = (
    field: 'storeName' | 'storeEmail' | 'storePhone' | 'sellerId',
    value: string,
  ) => {
    setStoreInfo((prev) => ({ ...prev, [field]: value }));
  };

  const onChangePhone = (value: string, info: MuiTelInputInfo) => {
    setStoreInfo((prev) => ({ ...prev, storePhone: value }));
    const digits = (info.nationalNumber ?? '').replace(/\D/g, '');
    setPhoneError(digits.length > 0 && digits.length < 7);
  };

  const onChangeCheckOptions = (name: 'createOrders') => {
    setCheckOptions((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const onChangeLogisticModes = (modes: string[]) => {
    setAutoCreateLogisticModes(modes);
  };

  const onChangeIsWebhookPrimary = (isPrimary: boolean) => {
    setIsWebhookPrimary(isPrimary);
  };

  const handleSelectAddress = (details: AddressDetails) => {
    setAddressData({
      address: details.address,
      country: details.country || 'MEX',
      latitude: details.latitude,
      longitude: details.longitude,
      zipCode: details.zipCode,
    });
  };

  const handleChangeAddressText = (text: string) => {
    setAddressData((prev) => ({ ...prev, address: text }));
  };

  const loadPluginConfig = useCallback(async () => {
    if (!clientId || !pluginId) return;
    setLoading(true);
    setPhoneError(false);
    try {
      const http = initializeHttpProvider();
      const response = await http.mlParcel.getPluginDetails({ clientId, pluginId });
      if ('success' in response && response.success) {
        const { localConfig } = (response as IPluginDetailResponse).data;
        setUseCustomAddress(Boolean(localConfig.useCollectAddress));
        setCheckOptions({ createOrders: Boolean(localConfig.autoCreateOrder) });
        setAutoCreateLogisticModes(
          Array.isArray(localConfig.autoCreateLogisticModes)
            ? localConfig.autoCreateLogisticModes
            : [],
        );
        setProcessOrdersWebhook(Boolean(localConfig.processOrdersWebhook));
        setStoreInfo({
          storeName: localConfig.storeName || '',
          storeEmail: localConfig.storeEmail || '',
          storePhone: normalizePhone(localConfig.storePhone || ''),
          sellerId: localConfig.sellerId || '',
        });
        setIsWebhookPrimary(localConfig.webhookPlatform === PLATFORM_NAME);
        if (localConfig.collectAddress) {
          setAddressData({
            address: localConfig.collectAddress.address || '',
            country: localConfig.collectAddress.country || 'MEX',
            latitude: localConfig.collectAddress.latitude || 0,
            longitude: localConfig.collectAddress.longitude || 0,
            zipCode: localConfig.collectAddress.zipCode || '',
          });
        }
      }
    } catch {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  }, [clientId, pluginId]);

  const onSavePluginConfig = async () => {
    if (!clientId || !pluginId) {
      toast.error('Faltan datos requeridos');
      return false;
    }
    if (phoneError) {
      toast.error('El teléfono de la tienda no es válido');
      return false;
    }
    setSaving(true);
    try {
      const http = initializeHttpProvider();
      const requestData: IUpdatePluginRequest = {
        local_config: {
          autoCreateOrder: checkOptions.createOrders,
          useCollectAddress: useCustomAddress,
          collectAddress: {
            address: useCustomAddress ? addressData.address : '',
            country: useCustomAddress ? addressData.country : '',
            latitude: useCustomAddress ? addressData.latitude : 0,
            longitude: useCustomAddress ? addressData.longitude : 0,
            references: '',
            zipCode: useCustomAddress ? addressData.zipCode : '',
          },
          sellerId: storeInfo.sellerId,
          storeEmail: storeInfo.storeEmail,
          storeName: storeInfo.storeName,
          storePhone: storeInfo.storePhone,
          webhookPlatform: isWebhookPrimary ? PLATFORM_NAME : null,
          autoCreateLogisticModes,
          processOrdersWebhook,
        },
      };
      const response = await http.mlParcel.updatePlugin({ clientId, pluginId, data: requestData });
      if ('success' in response && response.success) {
        toast.success('Configuración guardada exitosamente');
        return true;
      } else {
        toast.error('Error al guardar configuración');
        return false;
      }
    } catch {
      toast.error('Error al guardar configuración');
      return false;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (isOpen) loadPluginConfig();
  }, [isOpen, pluginId, clientId, loadPluginConfig]);

  return {
    useCustomAddress,
    onToggleAddress,
    checkOptions,
    onChangeCheckOptions,
    autoCreateLogisticModes,
    onChangeLogisticModes,
    storeInfo,
    onChangeStoreInfo,
    onChangePhone,
    phoneError,
    addressData,
    handleSelectAddress,
    handleChangeAddressText,
    isWebhookPrimary,
    onChangeIsWebhookPrimary,
    loading,
    saving,
    onSavePluginConfig,
  };
};

export default useMLParcelConfig;
